import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";

//Components
import { NotificationManager } from "Components/Notifications";
import { Alert } from "@mui/material";

//Controls
import { Button, Form, Modal, Select } from "@gpstrackit/gtc-react-controls";

//Actions
import {
  reset,
  editCameraManagementSearchUnits,
  editCameraManagementBulkUnPairing,
  editCameraManagementBulkPairing
} from "./redux/slice";
import { useSelector, useDispatch } from "react-redux";

//Language
import { useIntl } from "react-intl";

//Styles
import "./styles.scss";

const EditCameraManagementPairing = ({ refresh }) => {
  const dispatch = useDispatch();
  const { messages } = useIntl();

  //Local States
  const [selectedUnit, setSelectedUnit] = useState(null);
  const formRef = useRef();

  //Redux States
  const { open, loading, unit, units, pairing, unPairing } = useSelector(
    (state) => state.editCameraManagementPairingRedux
  );

  useEffect(() => {
    if (!open) return;
    dispatch(editCameraManagementSearchUnits());
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) return;

    if (unit?.linkedUnitId) {
      const linked =
        unit?.linkedUnit?.id ? unit?.linkedUnit : units.find((item) => item.id === unit.linkedUnitId);
      setSelectedUnit(linked || null);
      return;
    }

    setSelectedUnit(null);
  }, [open, unit, units]);

  useEffect(() => {
    if (pairing && pairing.length) {
      let result = pairing[0];
      if (!result?.errorMessage) {
        NotificationManager.success(
          messages["manage_cameraManagement_editPairing_pairingSuccessSave"],
          messages["success"],
          3000
        );
        refresh();
        handleClose();
      } else {
        let currentMessage = result?.errorMessage || messages["error"];
        NotificationManager.error(currentMessage, messages["error"], 3000);
        dispatch(reset({ pairing: null }));
      }
    }
    if (unPairing) {
      if (unPairing?.result) {
        NotificationManager.success(
          messages["manage_cameraManagement_editPairing_unPairingSuccessSave"],
          messages["success"],
          3000
        );
        refresh();
        handleClose();
      } else {
        dispatch(reset({ unPairing: null }));
        NotificationManager.error(messages["errorMessage"], messages["error"], 3000);
      }
    }
  }, [pairing, unPairing]);

  const handleClose = useCallback(() => {
    setSelectedUnit(null);
    dispatch(reset());
  }, [dispatch]);

  const applyChanges = useCallback(() => {
    if (unit?.linkedUnitId && !selectedUnit) {
      dispatch(editCameraManagementBulkUnPairing([unit?.id]));
    } else if (
      (unit?.linkedUnitId && unit?.linkedUnitId !== selectedUnit.id) ||
      (!unit?.linkedUnitId && selectedUnit.id)
    ) {
      dispatch(
        editCameraManagementBulkPairing([{ unitIdCamera: unit?.id, unitIdGps: selectedUnit.id }])
      );
    }
  }, [unit, selectedUnit]);

  const currentSelectedUnit = useMemo(
    () => (selectedUnit ? selectedUnit?.id : null),
    [selectedUnit]
  );
  const disabled = useMemo(
    () => unit?.linkedUnitId !== currentSelectedUnit,
    [unit, currentSelectedUnit]
  );

  return (
    <Modal
      title={`${messages["manage_cameraManagement_editPairing_title"]}  (${unit?.label})`}
      open={open}
      loading={loading}
      position="right"
      size="small"
      handleClose={() => handleClose()}
      footer={
        <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
          <Button
            style={{ marginRight: 8 }}
            isDisabled={loading}
            onClick={() => handleClose()}
            variant="secondary"
          >
            {messages["cancel"]}
          </Button>
          <Button
            isDisabled={loading || !disabled}
            onClick={(e) => {
              if (e) e.preventDefault();
              formRef.current.submit();
            }}
            variant="primary"
          >
            {messages["save"]}
          </Button>
        </div>
      }
    >
      <div style={{ padding: 24 }}>
        <Form
          ref={formRef}
          onValidSubmit={() => {
            applyChanges();
          }}
        >
          <Select
            id="unit"
            label={messages["manage_cameraManagement_editPairing_select"]}
            placeholder={messages["manage_cameraManagement_editPairing_select"]}
            valueField="id"
            textField="label"
            options={units.filter(
              (item) =>
                item.deviceModel?.manufacturer?.name?.toUpperCase() !== "VIDFLEET" &&
                item.deviceModel?.manufacturer?.name?.toUpperCase() !== "SMARTWITNESS"
            )}
            value={selectedUnit || null}
            onChange={(newUnit) => setSelectedUnit(newUnit)}
            isClearable
            isSearchable
          />
          {selectedUnit?.linkedByUnitId && selectedUnit?.id !== unit?.linkedUnitId && (
            <div className="cameras-manage-info-message">
              <Alert severity="warning" icon={false}>
                {messages["manage_cameraManagement_editPairing_warningMessageFirst"]}{" "}
                <strong>{selectedUnit?.linkedByUnit?.label}</strong>.{" "}
                {messages["manage_cameraManagement_editPairing_warningMessageSecond"]}{" "}
                <strong>{selectedUnit?.linkedByUnit?.label}</strong>{" "}
                {messages["manage_cameraManagement_editPairing_warningMessageThird"]}{" "}
                <strong>{unit?.label}</strong>
              </Alert>
            </div>
          )}
        </Form>
      </div>
    </Modal>
  );
};

export default EditCameraManagementPairing;
